import { supabase } from './supabase'

/**
 * Team map (Apex) — client data access.
 *
 * Reads come straight off `tournament_team_maps` / `tournament_teams` /
 * `tournament_team_members` (RLS: public read-only). Writes go exclusively
 * through the SECURITY DEFINER RPCs from migration
 * 20260826010000_tournament_team_map — organizers save the whole map at once,
 * players seat themselves via join_team / leave_team while self-assign is on
 * and the map is unlocked (5 minutes before kickoff, same as the lobby code).
 */

/** The map locks this many ms before the tournament starts. */
export const TEAM_MAP_LOCK_LEAD_MS = 5 * 60 * 1000

/** Apex Private Match caps a lobby at 60 players and won't start BR under 30. */
export const APEX_LOBBY_MAX = 60
export const APEX_BR_MIN = 30

export interface TeamMember {
  userId: string
  slot: number
  gamertag: string
  avatar: string | null
}

export interface Team {
  id: string
  /** 0-based; display "Team N" = index + 1 unless renamed. */
  index: number
  name: string
  members: TeamMember[]
}

export interface TeamMap {
  tournamentId: string
  teamCount: number
  teamSize: number
  selfAssign: boolean
  teams: Team[]
}

/** The tournament's team map with all teams and seats, or null when none exists. */
export async function getTeamMap(tournamentId: string): Promise<TeamMap | null> {
  const { data: m, error } = await supabase
    .from('tournament_team_maps')
    .select('tournament_id, team_count, team_size, self_assign')
    .eq('tournament_id', tournamentId)
    .maybeSingle()
  if (error || !m) {
    if (error) console.error('getTeamMap error:', error.message)
    return null
  }

  const [{ data: teams, error: tErr }, { data: members, error: mErr }] = await Promise.all([
    supabase
      .from('tournament_teams')
      .select('id, index, name')
      .eq('tournament_id', tournamentId)
      .order('index', { ascending: true }),
    supabase
      .from('tournament_team_members')
      .select('team_id, user_id, slot, gamertag, avatar')
      .eq('tournament_id', tournamentId)
      .order('slot', { ascending: true }),
  ])
  if (tErr || mErr) {
    console.error('getTeamMap error:', tErr?.message ?? mErr?.message)
    return null
  }

  const byTeam = new Map<string, TeamMember[]>()
  for (const r of (members ?? []) as Array<{
    team_id: string
    user_id: string
    slot: number
    gamertag: string
    avatar: string | null
  }>) {
    const list = byTeam.get(r.team_id) ?? []
    list.push({ userId: r.user_id, slot: r.slot, gamertag: r.gamertag, avatar: r.avatar })
    byTeam.set(r.team_id, list)
  }

  return {
    tournamentId: m.tournament_id as string,
    teamCount: m.team_count as number,
    teamSize: m.team_size as number,
    selfAssign: m.self_assign as boolean,
    teams: ((teams ?? []) as Array<{ id: string; index: number; name: string }>).map((t) => ({
      id: t.id,
      index: t.index,
      name: t.name,
      members: byTeam.get(t.id) ?? [],
    })),
  }
}

type Result = { ok: true } | { error: string }

async function rpc(name: string, params: Record<string, unknown>): Promise<Result> {
  const { error } = await supabase.rpc(name, params)
  if (error) {
    console.error(`${name} error:`, error.message)
    return { error: error.message }
  }
  return { ok: true }
}

/** (Re)generate the map from the event's teams × team size (organizer-only). */
export function generateTeamMap(tournamentId: string): Promise<Result> {
  return rpc('generate_team_map', { p_tournament_id: tournamentId })
}

/** Payload for `saveTeamMap`: every team's name and seats. */
export interface TeamMapSave {
  index: number
  name: string
  members: { slot: number; userId: string }[]
}

/** Save the whole map — names + every seat — in one call (organizer-only). */
export function saveTeamMap(tournamentId: string, teams: TeamMapSave[]): Promise<Result> {
  return rpc('save_team_map', {
    p_tournament_id: tournamentId,
    p_teams: teams.map((t) => ({
      index: t.index,
      name: t.name,
      members: t.members.map((m) => ({ slot: m.slot, user_id: m.userId })),
    })),
  })
}

export function setTeamMapSelfAssign(tournamentId: string, enabled: boolean): Promise<Result> {
  return rpc('set_team_map_self_assign', { p_tournament_id: tournamentId, p_enabled: enabled })
}

export function deleteTeamMap(tournamentId: string): Promise<Result> {
  return rpc('delete_team_map', { p_tournament_id: tournamentId })
}

/** Seat the current user in the first open slot of a team (self-assign). */
export function joinTeam(tournamentId: string, teamIndex: number): Promise<Result> {
  return rpc('join_team', { p_tournament_id: tournamentId, p_team_index: teamIndex })
}

/** Give up the current user's seat (self-assign). */
export function leaveTeam(tournamentId: string): Promise<Result> {
  return rpc('leave_team', { p_tournament_id: tournamentId })
}

/** Whether the map is locked for player self-assignment (client-side mirror of `team_map_locked`). */
export function isTeamMapLocked(startsAt: string, now = Date.now()): boolean {
  return now >= new Date(startsAt).getTime() - TEAM_MAP_LOCK_LEAD_MS
}

/**
 * Apex Private Match colours the 20 team panels in this order; reusing the ramp
 * means "Team 7" looks the same in GamerPlug as in the in-game admin panel.
 */
export const TEAM_COLORS = [
  '#17a8a8', '#2f5bff', '#3b3bd6', '#7d3cc9', '#a03aa6',
  '#c73a86', '#d63562', '#d62e2e', '#e0551f', '#e26a1a',
  '#e67a1a', '#e8901c', '#d99f1c', '#c9a21b', '#a9a51f',
  '#8aa521', '#65a52a', '#4aa53a', '#3fa64a', '#2fa860',
]

export function teamColor(index: number): string {
  return TEAM_COLORS[index % TEAM_COLORS.length]
}

/** Plain-text roster the organizer can read while seating players in-game. */
export function rosterText(map: TeamMap): string {
  return map.teams
    .map((t) => {
      const names = [...t.members].sort((a, b) => a.slot - b.slot).map((m) => m.gamertag)
      return `${t.name}: ${names.length ? names.join(', ') : '—'}`
    })
    .join('\n')
}
